import type {
  Conversation,
  SearchMatch,
  SlackBookmark,
  SlackDndInfo,
  SlackEmojiMap,
  SlackFile,
  SlackMessage,
  SlackPinItem,
  SlackProfile,
  SlackReminder,
  SlackResponse,
  SlackScheduledMessage,
  SlackTeam,
  SlackUser,
  SlackUserGroup,
} from './types'

export type Params = Record<string, string | number | boolean | undefined | null | object>

/**
 * Sends one Slack Web API call and resolves with the parsed JSON body.
 * Each platform supplies its own transport (server proxy, Electron IPC, native HTTP).
 */
export type Transport = (method: string, params: Params, token: string) => Promise<SlackResponse>

/** Uploads raw bytes to a URL returned by files.getUploadURLExternal. */
export type Uploader = (uploadUrl: string, file: Blob, filename: string) => Promise<void>

export class SlackApiError extends Error {
  constructor(
    public method: string,
    public code: string,
    public response?: SlackResponse,
  ) {
    super(`${method}: ${code}${response?.needed ? ` (needs ${response.needed})` : ''}`)
    this.name = 'SlackApiError'
  }
}

/** Serialises params as application/x-www-form-urlencoded, JSON-encoding nested values. */
export function encodeParams(params: Params): URLSearchParams {
  const body = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue
    body.set(key, typeof value === 'object' ? JSON.stringify(value) : String(value))
  }
  return body
}

/**
 * A transport built on fetch. With `baseUrl` = https://slack.com/api the token travels
 * in the form body, which keeps the request "simple" (no CORS preflight).
 */
export function createFetchTransport(options: {
  baseUrl?: string
  fetch?: typeof fetch
  /** Send the token as a Bearer header instead of in the body. */
  bearer?: boolean
} = {}): Transport {
  const baseUrl = options.baseUrl ?? 'https://slack.com/api'
  const doFetch = options.fetch ?? ((...args: Parameters<typeof fetch>) => fetch(...args))
  return async (method, params, token) => {
    const body = encodeParams(options.bearer ? params : { ...params, token })
    const res = await doFetch(`${baseUrl}/${method}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        ...(options.bearer ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: body.toString(),
    })
    if (res.status === 429) {
      return { ok: false, error: 'ratelimited', retry_after: Number(res.headers.get('Retry-After') ?? 1) }
    }
    return (await res.json()) as SlackResponse
  }
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

export class SlackClient {
  constructor(
    public readonly token: string,
    private readonly transport: Transport,
    private readonly uploader?: Uploader,
  ) {}

  /** Calls any Web API method, retrying on rate limits. Throws SlackApiError when ok=false. */
  async call<T = SlackResponse>(method: string, params: Params = {}): Promise<T & SlackResponse> {
    for (let attempt = 0; ; attempt++) {
      const res = await this.transport(method, params, this.token)
      if (res.ok) return res as T & SlackResponse
      if (res.error === 'ratelimited' && attempt < 3) {
        await sleep(Number(res.retry_after ?? 1) * 1000 + 250)
        continue
      }
      throw new SlackApiError(method, res.error ?? 'unknown_error', res)
    }
  }

  /** Follows cursor pagination, collecting `key` from every page. */
  async paginate<T>(method: string, key: string, params: Params = {}, maxPages = 50): Promise<T[]> {
    const items: T[] = []
    let cursor: string | undefined
    for (let page = 0; page < maxPages; page++) {
      const res = await this.call(method, { limit: 200, ...params, cursor })
      items.push(...((res[key] as T[]) ?? []))
      cursor = res.response_metadata?.next_cursor
      if (!cursor) break
    }
    return items
  }

  // ---------------------------------------------------------------- auth / team
  auth = {
    test: () => this.call<{ url: string; team: string; user: string; team_id: string; user_id: string; enterprise_id?: string }>('auth.test'),
    revoke: () => this.call('auth.revoke'),
  }

  team = {
    info: (team?: string) => this.call<{ team: SlackTeam }>('team.info', { team }).then((r) => r.team),
  }

  // ---------------------------------------------------------------- users
  users = {
    list: () => this.paginate<SlackUser>('users.list', 'members'),
    info: (user: string) => this.call<{ user: SlackUser }>('users.info', { user }).then((r) => r.user),
    lookupByEmail: (email: string) => this.call<{ user: SlackUser }>('users.lookupByEmail', { email }).then((r) => r.user),
    conversations: (types = 'public_channel,private_channel,mpim,im') =>
      this.paginate<Conversation>('users.conversations', 'channels', { types, exclude_archived: true }),
    getPresence: (user: string) => this.call<{ presence: 'active' | 'away' }>('users.getPresence', { user }),
    setPresence: (presence: 'auto' | 'away') => this.call('users.setPresence', { presence }),
    profile: {
      get: (user?: string) => this.call<{ profile: SlackProfile }>('users.profile.get', { user }).then((r) => r.profile),
      set: (profile: Partial<SlackProfile>) =>
        this.call<{ profile: SlackProfile }>('users.profile.set', { profile }).then((r) => r.profile),
    },
  }

  // ---------------------------------------------------------------- conversations
  conversations = {
    list: (types = 'public_channel', excludeArchived = true) =>
      this.paginate<Conversation>('conversations.list', 'channels', { types, exclude_archived: excludeArchived }),
    info: (channel: string) =>
      this.call<{ channel: Conversation }>('conversations.info', { channel, include_num_members: true }).then((r) => r.channel),
    history: (channel: string, opts: { cursor?: string; latest?: string; oldest?: string; inclusive?: boolean; limit?: number } = {}) =>
      this.call<{ messages: SlackMessage[]; has_more: boolean }>('conversations.history', { channel, limit: 50, ...opts }),
    replies: (channel: string, ts: string, opts: { cursor?: string; limit?: number } = {}) =>
      this.call<{ messages: SlackMessage[]; has_more: boolean }>('conversations.replies', { channel, ts, limit: 200, ...opts }),
    members: (channel: string) => this.paginate<string>('conversations.members', 'members', { channel }),
    create: (name: string, isPrivate = false) =>
      this.call<{ channel: Conversation }>('conversations.create', { name, is_private: isPrivate }).then((r) => r.channel),
    rename: (channel: string, name: string) =>
      this.call<{ channel: Conversation }>('conversations.rename', { channel, name }).then((r) => r.channel),
    setTopic: (channel: string, topic: string) => this.call('conversations.setTopic', { channel, topic }),
    setPurpose: (channel: string, purpose: string) => this.call('conversations.setPurpose', { channel, purpose }),
    archive: (channel: string) => this.call('conversations.archive', { channel }),
    unarchive: (channel: string) => this.call('conversations.unarchive', { channel }),
    join: (channel: string) => this.call<{ channel: Conversation }>('conversations.join', { channel }).then((r) => r.channel),
    leave: (channel: string) => this.call('conversations.leave', { channel }),
    invite: (channel: string, users: string[]) =>
      this.call<{ channel: Conversation }>('conversations.invite', { channel, users: users.join(',') }),
    kick: (channel: string, user: string) => this.call('conversations.kick', { channel, user }),
    open: (users: string[]) =>
      this.call<{ channel: Conversation }>('conversations.open', { users: users.join(','), return_im: true }).then((r) => r.channel),
    close: (channel: string) => this.call('conversations.close', { channel }),
    mark: (channel: string, ts: string) => this.call('conversations.mark', { channel, ts }),
  }

  // ---------------------------------------------------------------- chat
  chat = {
    postMessage: (channel: string, text: string, opts: { thread_ts?: string; reply_broadcast?: boolean; blocks?: object[] } = {}) =>
      this.call<{ ts: string; channel: string; message: SlackMessage }>('chat.postMessage', {
        channel,
        text,
        link_names: true,
        ...opts,
      }),
    meMessage: (channel: string, text: string) => this.call('chat.meMessage', { channel, text }),
    update: (channel: string, ts: string, text: string) =>
      this.call<{ ts: string; text: string; message: SlackMessage }>('chat.update', { channel, ts, text, link_names: true }),
    delete: (channel: string, ts: string) => this.call('chat.delete', { channel, ts }),
    getPermalink: (channel: string, message_ts: string) =>
      this.call<{ permalink: string }>('chat.getPermalink', { channel, message_ts }).then((r) => r.permalink),
    scheduleMessage: (channel: string, text: string, post_at: number, thread_ts?: string) =>
      this.call<{ scheduled_message_id: string; post_at: number }>('chat.scheduleMessage', { channel, text, post_at, thread_ts, link_names: true }),
    scheduledMessages: (channel?: string) =>
      this.paginate<SlackScheduledMessage>('chat.scheduledMessages.list', 'scheduled_messages', { channel }),
    deleteScheduledMessage: (channel: string, scheduled_message_id: string) =>
      this.call('chat.deleteScheduledMessage', { channel, scheduled_message_id }),
  }

  // ---------------------------------------------------------------- reactions / pins / bookmarks
  reactions = {
    add: (channel: string, timestamp: string, name: string) => this.call('reactions.add', { channel, timestamp, name }),
    remove: (channel: string, timestamp: string, name: string) => this.call('reactions.remove', { channel, timestamp, name }),
  }

  pins = {
    list: (channel: string) => this.call<{ items: SlackPinItem[] }>('pins.list', { channel }).then((r) => r.items ?? []),
    add: (channel: string, timestamp: string) => this.call('pins.add', { channel, timestamp }),
    remove: (channel: string, timestamp: string) => this.call('pins.remove', { channel, timestamp }),
  }

  bookmarks = {
    list: (channel_id: string) =>
      this.call<{ bookmarks: SlackBookmark[] }>('bookmarks.list', { channel_id }).then((r) => r.bookmarks ?? []),
    add: (channel_id: string, title: string, link: string, emoji?: string) =>
      this.call<{ bookmark: SlackBookmark }>('bookmarks.add', { channel_id, title, link, emoji, type: 'link' }),
    edit: (channel_id: string, bookmark_id: string, changes: { title?: string; link?: string; emoji?: string }) =>
      this.call('bookmarks.edit', { channel_id, bookmark_id, ...changes }),
    remove: (channel_id: string, bookmark_id: string) => this.call('bookmarks.remove', { channel_id, bookmark_id }),
  }

  // ---------------------------------------------------------------- search
  search = {
    messages: (query: string, page = 1) =>
      this.call<{ messages: { matches: SearchMatch[]; total: number; paging: { pages: number; page: number } } }>(
        'search.messages',
        { query, page, count: 40, sort: 'timestamp', highlight: false },
      ).then((r) => r.messages),
    files: (query: string, page = 1) =>
      this.call<{ files: { matches: SlackFile[]; total: number } }>('search.files', { query, page, count: 40 }).then((r) => r.files),
  }

  // ---------------------------------------------------------------- files
  files = {
    list: (opts: { channel?: string; user?: string; page?: number; types?: string } = {}) =>
      this.call<{ files: SlackFile[]; paging: { pages: number; page: number } }>('files.list', { count: 50, ...opts }),
    info: (file: string) => this.call<{ file: SlackFile }>('files.info', { file }).then((r) => r.file),
    delete: (file: string) => this.call('files.delete', { file }),
    /** Uploads a file using the two-step external upload flow and shares it to a channel. */
    upload: async (file: Blob, filename: string, opts: { channel?: string; initial_comment?: string; thread_ts?: string; title?: string } = {}) => {
      if (!this.uploader) throw new Error('No uploader configured')
      const { upload_url, file_id } = await this.call<{ upload_url: string; file_id: string }>('files.getUploadURLExternal', {
        filename,
        length: file.size,
      })
      await this.uploader(upload_url, file, filename)
      return this.call<{ files: SlackFile[] }>('files.completeUploadExternal', {
        files: [{ id: file_id, title: opts.title ?? filename }],
        channel_id: opts.channel,
        initial_comment: opts.initial_comment,
        thread_ts: opts.thread_ts,
      })
    },
  }

  // ---------------------------------------------------------------- misc
  emoji = {
    list: () => this.call<{ emoji: SlackEmojiMap }>('emoji.list').then((r) => r.emoji),
  }

  reminders = {
    list: () => this.call<{ reminders: SlackReminder[] }>('reminders.list').then((r) => r.reminders ?? []),
    add: (text: string, time: string | number) =>
      this.call<{ reminder: SlackReminder }>('reminders.add', { text, time }).then((r) => r.reminder),
    complete: (reminder: string) => this.call('reminders.complete', { reminder }),
    delete: (reminder: string) => this.call('reminders.delete', { reminder }),
  }

  dnd = {
    info: (user?: string) => this.call<SlackDndInfo>('dnd.info', { user }),
    setSnooze: (num_minutes: number) => this.call<SlackDndInfo>('dnd.setSnooze', { num_minutes }),
    endSnooze: () => this.call<SlackDndInfo>('dnd.endSnooze'),
  }

  usergroups = {
    list: () =>
      this.call<{ usergroups: SlackUserGroup[] }>('usergroups.list', { include_users: true }).then((r) => r.usergroups ?? []),
  }
}
